import { IconButton } from '@atlaskit/button/new';
import SearchIcon from '@atlaskit/icon/core/search';
import { xcss } from '@atlaskit/primitives';
import Box from '@atlaskit/primitives/box';
import { token } from '@atlaskit/tokens';
import { messages } from '@databases/intl/messages';
import { useDebouncedCallback } from '@databases/react';
import { SearchBar } from '@databases/search-bar/SearchBar';
import { type DatabaseViewSettings } from '@databases/types/database/view';
import React, { type FunctionComponent, useCallback, useRef, useState } from 'react';
import { useIntl } from 'react-intl-next';

import { isFeatureFlagEnabled } from '@canvas/client-feature-flags';

import { isAndroid } from '../../../utils';

export interface MobileQuickSearchProps {
  value?: DatabaseViewSettings['search'];
  onChange: (value: DatabaseViewSettings['search']) => void;
  tooltip: React.FC<{ children: React.ReactNode }>;
  onHandleExpandField: () => void;
}

const searchFieldCollapsedStyles = xcss({
  width: '32px',
  transition: 'width 0.3s, background 0.3s',
});

const searchFieldExpandedStyles = xcss({
  width: `calc(100% - ${token('space.400', '32px')})`,
  position: 'absolute',
  right: token('space.200', '16px'),
  bottom: token('space.0', '0px'),
  transition: 'width 0.3s, background 0.3s',
});

const searchBarCollapsedStyles = xcss({
  width: '32px',
});

const searchBarExpandedStyles = xcss({
  width: '100%',
  position: 'absolute',
  height: token('space.600', '48px'),
});

const searchFieldTransition = (expanded: boolean) => {
  return expanded ? searchFieldExpandedStyles : searchFieldCollapsedStyles;
};

const searchBarWrapper = (expanded: boolean) => {
  return expanded ? searchBarExpandedStyles : searchBarCollapsedStyles;
};

const MobileQuickSearch: FunctionComponent<MobileQuickSearchProps> = ({
  onChange: onChangeProp,
  value: valueProp,
  tooltip: IconToolTip,
  onHandleExpandField,
}) => {
  const [value, setLocalValue] = useState(valueProp);
  const debouncedOnChanged = useDebouncedCallback(onChangeProp, 500);
  const isEmpty = !value;
  const isDatabaseContentWrapperEnabled = isFeatureFlagEnabled(
    'databases-frontend-content-wrapper'
  );

  /**
   * The local value should be updated immediately, but the callback
   * to trigger the search should be debounced.
   */
  const updateValue = useCallback(
    (value?: string) => {
      setLocalValue(value);
      debouncedOnChanged(value);
    },
    [setLocalValue, debouncedOnChanged]
  );

  const searchFieldRef = useRef<HTMLInputElement>(null);

  const [expanded, setExpanded] = useState(!isEmpty);

  const { formatMessage } = useIntl();

  const handleExpandField = useCallback(() => {
    onHandleExpandField();
    setExpanded(true);
  }, [onHandleExpandField]);

  const handleBlur = useCallback(() => {
    isEmpty && setExpanded(false);
  }, [isEmpty]);

  const handleClear = useCallback(() => {
    updateValue(undefined);
    searchFieldRef.current?.focus();
    setExpanded(false);
  }, [updateValue]);

  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      if (!expanded) {
        handleExpandField();
      }

      const value = e.target.value;

      updateValue(value);
    },
    [expanded, handleExpandField, updateValue]
  );

  return (
    <Box xcss={searchFieldTransition(expanded)}>
      {expanded ? (
        <Box xcss={searchBarWrapper(expanded)}>
          <SearchBar
            searchFieldRef={searchFieldRef}
            value={value}
            handleOnChange={handleChange}
            handleOnClear={handleClear}
            placeholder={formatMessage(messages['quick-search.search-database-text'])}
            autoComplete="off"
            autoFocus
            cancelButtonAlwaysOn
            onBlur={handleBlur}
          />
        </Box>
      ) : (
        <IconToolTip>
          <IconButton
            icon={(iconProps) => <SearchIcon {...iconProps} size="medium" />}
            label={formatMessage(messages['quick-search.search-database-text'])}
            onClick={handleExpandField}
            appearance={isDatabaseContentWrapperEnabled ? 'subtle' : 'default'}
          />
        </IconToolTip>
      )}
    </Box>
  );
};

/**
 * Wrapper component that handles Android-specific behavior for mobile search.
 * On Android mobile devices, search functionality is disabled.
 */
export const MobileSearchWrapper: FunctionComponent<MobileQuickSearchProps> = (props) => {
  if (isAndroid) {
    return null;
  }

  return <MobileQuickSearch {...props} />;
};

export default MobileQuickSearch;
